import {Button, ButtonProps} from '@mui/material';
import {ElementType} from 'react';
import {useFormState} from 'react-hook-form';
import {ComponentReturnValue, PropsWithOptionalChildren} from '../react/types';
import {useFormHandlers} from './Form';

type AllowedButtonProps<Element extends ElementType> = Omit<ButtonProps<Element>, 'onClick' | 'type'>;
export type SubmitButtonProps<Element extends ElementType = typeof Button> = AllowedButtonProps<Element> & {
  alwaysEnabled?: boolean;
};

export const SubmitButton: <Element extends ElementType>(
  props: PropsWithOptionalChildren<SubmitButtonProps<Element>>,
) => ComponentReturnValue = ({
  alwaysEnabled = false,
  children,
  disabled = false,
  variant = 'contained',
  ...muiProps
}: SubmitButtonProps) => {
  const {isDirty, isSubmitting, isValid, isValidating} = useFormState();
  const {doSubmit} = useFormHandlers();
  const enabled = computeEnabled(alwaysEnabled, disabled, isDirty, isSubmitting, isValid, isValidating);
  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <Button {...muiProps} disabled={!enabled} onClick={doSubmit} type="submit" variant={variant}>
      {children}
    </Button>
  );
};

function computeEnabled(
  alwaysEnabled: boolean,
  disabled: boolean,
  isDirty: boolean,
  isSubmitting: boolean,
  isValid: boolean,
  isValidating: boolean,
) {
  // The below logic could be implemented as a single boolean expression,
  // but it's broken out into separate blocks to make it easier to understand.

  // Short-circuit the rest of the logic if the button should always be enabled:
  if (alwaysEnabled) {
    return true;
  }

  // Not enabled if the disabled prop is set or if the form is currently submitting or validating:
  if (disabled || isSubmitting || isValidating) {
    return false;
  }

  // To be enabled the form must be both valid _and_ dirty (dirty = modified by the user, not just initial values set)
  return isDirty && isValid;
}
